import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Minus, Plus } from 'lucide-react'
import type { OrderItem } from '@/types'

export interface SplitAssignmentRowProps {
  item: OrderItem
  guestCount: number
  /** units per guest, index 0 = guest 1 */
  units: number[]
  onChange: (guestIdx: number, delta: number) => void
  formatCurrency: (n: number) => string
}

/**
 * One order line in the "By items" assignment grid. Single-unit lines act
 * like a radio row (tap a guest to hand them the item); multi-unit lines get
 * a per-guest − / + stepper so 3 Cokes can go 1/1/1.
 */
export function SplitAssignmentRow({
  item,
  guestCount,
  units,
  onChange,
  formatCurrency,
}: SplitAssignmentRowProps) {
  const assigned = units.reduce((a, b) => a + b, 0)
  const remaining = item.quantity - assigned
  const net = item.total_price - (item.discount_amount ?? 0)
  const singleUnit = item.quantity === 1

  return (
    <div
      className={cn(
        'rounded-lg border p-2.5',
        remaining > 0 ? 'border-amber-400/60 bg-amber-50/40 dark:bg-amber-950/20' : 'border-border bg-card'
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium">
          {item.product?.name ?? 'Item'}
          {item.quantity > 1 && (
            <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">× {item.quantity}</span>
          )}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{formatCurrency(net)}</span>
      </div>
      {remaining > 0 && (
        <p className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
          {remaining} unit{remaining === 1 ? '' : 's'} unassigned
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {Array.from({ length: guestCount }, (_, g) => {
          const u = units[g] ?? 0
          if (singleUnit) {
            return (
              <Button
                key={g}
                type="button"
                variant={u > 0 ? 'default' : 'outline'}
                size="sm"
                className="h-8 px-2.5 text-xs font-semibold"
                onClick={() => onChange(g, u > 0 ? -1 : 1)}
              >
                G{g + 1}
              </Button>
            )
          }
          return (
            <div
              key={g}
              className={cn(
                'flex items-center gap-0.5 rounded-md border px-1 py-0.5',
                u > 0 ? 'border-primary/60 bg-primary/5' : 'border-border'
              )}
            >
              <span className="px-1 text-[11px] font-semibold text-muted-foreground">G{g + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={u <= 0}
                onClick={() => onChange(g, -1)}
                aria-label={`Remove one unit from guest ${g + 1}`}
              >
                <Minus className="h-3 w-3" aria-hidden />
              </Button>
              <span className="w-4 text-center text-xs font-bold tabular-nums">{u}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={remaining <= 0}
                onClick={() => onChange(g, 1)}
                aria-label={`Add one unit to guest ${g + 1}`}
              >
                <Plus className="h-3 w-3" aria-hidden />
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
